import { useState } from "react";
import { Modal } from "react-bootstrap";
import type { PopulationSegment } from "@models/populationSegment.model";

type Props = {
  objectiveName: string;
  segments: PopulationSegment[];
  isSaving: boolean;
  onClose: () => void;
  onSubmit: (name: string, populationSegmentId: number | null, explicitNone: boolean) => void;
};

// "No population" as an active choice (lookalike / DB-fed) — distinct from
// "Choose later", so linking health knows this campaign is not a gap.
const EXPLICIT_NONE = "none";

export default function CampaignFormModal({
  objectiveName,
  segments,
  isSaving,
  onClose,
  onSubmit,
}: Props) {
  const [name, setName] = useState("");
  const [segmentValue, setSegmentValue] = useState("");

  const canSubmit = name.trim().length > 0 && !isSaving;
  const segmentId =
    segmentValue && segmentValue !== EXPLICIT_NONE ? Number(segmentValue) : null;
  const selectedSegment = segmentId != null ? segments.find((s) => s.id === segmentId) ?? null : null;

  return (
    <Modal show onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "1rem" }}>New campaign</Modal.Title>
      </Modal.Header>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) onSubmit(name.trim(), segmentId, segmentValue === EXPLICIT_NONE);
        }}
      >
        <Modal.Body>
          {/* The objective comes from the section the campaign is created in,
              so it is stated rather than picked. */}
          <div className="small text-muted mb-3">
            Objective: <span className="fw-semibold">{objectiveName}</span>
          </div>

          <label htmlFor="campaignName" className="form-label small fw-semibold">
            Campaign name
          </label>
          <input
            id="campaignName"
            className="form-control"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Fall 2026 Enrollment"
            autoFocus
          />

          <label htmlFor="campaignSegment" className="form-label small fw-semibold mt-3">
            Population segment <span className="text-muted fw-normal">(optional)</span>
          </label>
          <select
            id="campaignSegment"
            className="form-select"
            value={segmentValue}
            onChange={(e) => setSegmentValue(e.target.value)}
          >
            <option value="">Choose later</option>
            <option value={EXPLICIT_NONE}>No population — lookalike / DB-fed</option>
            {segments.map((segment) => (
              <option key={segment.id} value={segment.id}>
                {segment.name}
                {segment.launch_count > 0
                  ? ` — in use by ${segment.launch_count} campaign${segment.launch_count === 1 ? "" : "s"}`
                  : ""}
              </option>
            ))}
          </select>
          {/* Sharing a population is allowed — campaigns then draw from the same
              roster — but an accidental pick of an in-use one shouldn't be silent. */}
          {selectedSegment && selectedSegment.launch_count > 0 && (
            <div className="alert alert-warning d-flex align-items-start gap-2 mt-2 mb-0 py-2 small">
              <i className="bi bi-exclamation-triangle-fill" />
              <div>
                <strong>{selectedSegment.name}</strong> is already in use by{" "}
                {selectedSegment.launch_count}{" "}
                {selectedSegment.launch_count === 1 ? "campaign" : "campaigns"}. This
                campaign will share the same roster of people. Make sure this is the
                population you mean — you can still change it later.
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn btn-sm btn-primary" disabled={!canSubmit}>
            Create campaign
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}
